// roll-sound-route.ts — a roll's sound ROUTE, built in the browser (owner 2026-09-10: "all effects are never queries").
//
// A roll frame carries only a sound REFERENCE (`roll_sound` = {v:2, rev, used?, voice?}, audio_helpers.php ft_roll_sound).
// This module turns the frame's ids into the ordered tiers of audio-catalog pool keys the take is picked from:
//   - the ITEM's own sounds come from its compiled `assets.audio` refs in the encounter's `.catalog` (or, for a cached
//     definition older than the refs, the same refs derived from its layers with the catalog's taxonomy);
//   - the creature / encounter facts (who attacked, who died, the ground underfoot, the target's vitality) come from
//     the encounter blob the page already holds;
//   - everything static (silent types, event cue tables, status stack bases, voice id → pool slug) comes from the audio
//     catalog body's `rules` (audio_helpers.php ft_audio_roll_rules — the one source).
// Browser presentation owns these decisions. Tier semantics: the first tier with any take wins; a tier is a union.

import { audioRefs, deriveAudioRefs, layerOf, type AudioCatalog, type AudioPhase, type AudioTaxonomy, type AudioTiers } from './audio-catalog';
import { isCastEffectRow } from '../janus-engine/presentation/cast-commit';

type Rec = Record<string, unknown>;
export interface RollSoundRules {
    v: 1;
    rollTypes: Record<string, string>; essence: Record<string, string>; statusBase: Record<string, string>; ids: Record<string, string>;
    weather: Record<string, string>; light: Record<string, string>; speed: Record<string, string>; composition: Record<string, string>;
    rubbleRemap: Record<string, string>; noRubble: string[]; customAbility: string[]; silent: string[];
    partyVote: Record<string, string>; magicFlow: Record<string, string[]>; eventCues: Record<string, string[]>;
    voiceAliases: Record<string, string>; actionEmotes: Record<string, string>; impact: { chance: number; heavy: number; kill?: number; thanks?: number; resisted?: number };
    voices: Record<string, string>;
}
export interface RollSoundContext {
    rules: RollSoundRules;
    taxonomy: AudioTaxonomy | null;
    /** A compiled item definition by id — the encounter's `.catalog` first. */
    def(id: string): Rec | null;
    /** An encounter creature row by encounter_creature_id. */
    row(id: string): Rec | null;
    /** An inventory row (on the map or carried) by inventory_id. */
    inventory(id: string): Rec | null;
    /** The roll's encounter blob (null off-encounter). */
    encounter: Rec | null;
    /** Read-only roster for callers that build a route directly. */
    creatures?: readonly Rec[];
    /** Conscious same-team candidates, reduced to arrival facts before the catalog await. */
    allies?: readonly AllyFacts[];
}
interface AllyFacts { id: string; dead: boolean; pc: boolean; voiceId: string; statuses: string }
/** `stack`: play the caster's line AND then the target's (a stealth spot or heal); otherwise the target speaks only when the caster has no take. */
export interface RollRoute { sfx: AudioTiers; voice?: { caster?: AudioTiers; once?: string; target?: AudioTiers; stack?: true; queue?: true; casterAfter?: { tiers: AudioTiers; once: string }; ally?: { tiers: AudioTiers; once: string } } }

const rec = (v: unknown): Rec | null => (v && typeof v === 'object' && !Array.isArray(v) ? v as Rec : null);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const has = (o: Rec, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);
/** A PHP-shaped scalar: [0]-wrapped and {items:{item_id}} catalog shells unwrapped. */
function unwrap(v: unknown): unknown {
    while (Array.isArray(v)) v = v[0];
    const shell = rec(rec(v)?.items);
    return shell && typeof shell.item_id === 'string' ? shell.item_id : v;
}
function text(v: unknown): string {
    v = unwrap(v);
    return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
}
const up = (v: unknown): string => text(v).trim().toUpperCase();
/** PHP (int) of a column value. */
function int(v: unknown): number {
    v = unwrap(v);
    if (typeof v === 'boolean') return v ? 1 : 0;
    const n = Math.trunc(Number(typeof v === 'string' || typeof v === 'number' ? v : NaN));
    return Number.isFinite(n) ? n : 0;
}
/** PHP !empty(). */
function truthy(v: unknown): boolean {
    v = unwrap(v);
    return !(v === null || v === undefined || v === '' || v === 0 || v === '0' || v === false);
}

// ── readers (the PHP helpers get_audio_info() reads through) ─────────────────────────────────────────────────
function itemField(arr: unknown, key: 'item_id' | 'item_type' | 'item_name'): string {
    const a = rec(arr);
    if (!a) return '';
    if (key === 'item_type' && text(rec(a.encounter_creatures)?.item_type) === 'lair') return 'lair';
    const items = rec(a.items);
    if (items && has(items, key)) return text(items[key]);
    const pc = rec(a.player_characters);
    if (pc) return key === 'item_type' ? 'player_character' : text(key === 'item_id' ? (pc.item_id ?? pc.player_character_id) : pc.player_name);
    return text(a[key]);
}
/** encounter_field(): the encounters bucket, then the legacy top-level slot. */
function encField(enc: Rec | null, key: string): unknown {
    if (!enc) return null;
    const bucket = rec(enc.encounters);
    const v = bucket && has(bucket, key) ? bucket[key] : has(enc, key) ? enc[key] : null;
    return unwrap(v) ?? null;
}
/** layer_value() on an encounter: the locations, campaigns and encounters buckets in that order, then top level. */
function encLayer(enc: Rec | null, key: string): unknown {
    if (!enc) return null;
    for (const name of ['locations', 'campaigns', 'encounters']) {
        const bucket = rec(enc[name]);
        if (bucket && has(bucket, key)) return unwrap(bucket[key]) ?? null;
    }
    return has(enc, key) ? unwrap(enc[key]) ?? null : null;
}
function statusIds(arr: unknown): string[] {
    const a = rec(arr);
    if (!a) return [];
    const reader = (globalThis as unknown as { ftCreatureStatuses?: (x: unknown, m: string) => unknown }).ftCreatureStatuses;
    let list: unknown = typeof reader === 'function' ? reader(a, 'target') : rec(a.statuses)?.target;
    if (!Array.isArray(list)) list = list ? [list] : [];
    return (list as unknown[]).map(e => {
        const r = rec(e);
        if (!r) return '';
        return up(r.status_item_id) || up(rec(r.items)?.item_id) || up(rec(rec(r.status_arr)?.items)?.item_id)
            || (typeof r.status_arr === 'string' ? up(r.status_arr) : '') || up(r.item_id);
    }).filter(Boolean);
}
function vitality(arr: unknown, key: string): number {
    const a = rec(arr);
    const computed = rec(a?.computed);
    const bucket = rec(computed?.vitality) ?? rec(computed?.v_arr) ?? rec(a?.vitality);
    return int(bucket?.[key]);
}
function actorId(arr: unknown): string {
    const a = rec(arr);
    return a ? text((rec(a.encounter_creatures) ?? a).encounter_creature_id).trim() : '';
}
function teamOf(arr: unknown): string {
    const a = rec(arr);
    if (!a) return '';
    return up(rec(a.encounter_creatures)?.team) || up(a.team) || layerOf(a, 'team');
}
function isDead(arr: unknown): boolean {
    return int(rec(rec(arr)?.encounter_creatures)?.encounter_creature_dead) === 1;
}
function voiceIdOf(arr: unknown): string {
    const a = rec(arr);
    if (!a) return '';
    const id = text((rec(a.encounter_creatures) ?? a).voice_id).trim();
    return UUID.test(id) ? id.toUpperCase() : '';
}
function isPlayerCharacter(arr: unknown): boolean {
    const a = rec(arr);
    return !!a && (itemField(a, 'item_type') === 'player_character' || (a.player_characters !== null && typeof a.player_characters === 'object'));
}
function inventoryTemplateId(inv: Rec | null): string {
    if (!inv) return '';
    return up(rec(inv.inventory)?.inventory_item_id) || up(inv.inventory_item_id) || up(itemField(inv, 'item_id'));
}

/** Cached refs can omit a phase before its layers were attached. Derive that phase from held layers. */
function phaseTiers(ctx: RollSoundContext, item: Rec | null, phase: AudioPhase): AudioTiers {
    if (!item) return [];
    const id = up(itemField(item, 'item_id'));
    const tiers = audioRefs(item)?.[phase] ?? deriveAudioRefs(item, ctx.taxonomy)?.[phase] ?? [];
    return tiers.map(t => t.map(k => (k === '@' ? id : k.startsWith('@/') ? id + k.slice(1) : k)));
}

export function fnv1a32(s: string): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return h >>> 0;
}

/** The catalog body's `rules`, normalised (PHP encodes an empty map as []); null when absent or malformed. */
export function rollSoundRules(value: unknown): RollSoundRules | null {
    const r = rec(value);
    if (!r || r.v !== 1) return null;
    const map = (v: unknown): Record<string, never> | null => (rec(v) as Record<string, never> | null) ?? (Array.isArray(v) && v.length === 0 ? {} : null);
    const list = (v: unknown): string[] | null => (Array.isArray(v) && v.every(x => typeof x === 'string') ? v as string[] : null);
    const out: Rec = { v: 1 };
    for (const k of ['rollTypes', 'essence', 'statusBase', 'ids', 'weather', 'light', 'speed', 'composition', 'rubbleRemap',
        'partyVote', 'magicFlow', 'eventCues', 'voiceAliases', 'actionEmotes', 'impact', 'voices']) {
        const m = map(r[k]);
        if (!m) return null;
        out[k] = m;
    }
    for (const k of ['noRubble', 'customAbility', 'silent']) {
        const l = list(r[k]);
        if (!l) return null;
        out[k] = l;
    }
    return out as unknown as RollSoundRules;
}

interface Env {
    roll: Rec; ctx: RollSoundContext; R: RollSoundRules; t1: string; t2: string; rid: string; used: Rec | null; noUsed: boolean; by: Rec | null; to: Rec | null; enc: Rec | null;
    /** Client-truth lane only: the attack it resolved and its kept natural d20 (its synthetic roll has no rolls_full). */
    attack?: { natural: number };
}

function gather(roll: Rec, ctx: RollSoundContext): Env {
    const sound = rec(roll.roll_sound);
    const rid = text(roll.roll_item_id);
    const template = (inv: Rec | null): Rec | null => { const t = inventoryTemplateId(inv); return t ? ctx.def(t) : null; };
    // item_id_used: the frame names it only when it is not roll_item_id (a weapon attack logs the inventory id), and says
    // `used: 0` when the row used none — then roll_item_id is NOT read as one (a GM status apply must not speak an emote).
    const noUsed = sound?.used === 0;
    const usedId = noUsed ? '' : (up(sound?.used) || up(rid));
    const heldTemplate = up(roll.roll_item_id_inventory_item_id);
    const used = usedId ? (ctx.def(usedId) ?? template(ctx.inventory(usedId)) ?? (heldTemplate ? ctx.def(heldTemplate) : null)) : null;
    const side = (id: unknown): Rec | null => { const k = up(id); return k ? (ctx.row(k) ?? ctx.inventory(k)) : null; };
    return { roll, ctx, R: ctx.rules, t1: text(roll.roll_type_1), t2: text(roll.roll_type_2), rid, used, noUsed, by: side(roll.roll_by), to: side(roll.roll_to), enc: ctx.encounter };
}

function dedupe(tiers: AudioTiers): AudioTiers {
    const seen = new Set<string>();
    return tiers.filter(t => { const k = t.join('\x1f'); if (seen.has(k)) return false; seen.add(k); return true; });
}

/** skill rows: which essence the roll used, from rolls_full ("Body Essence" → 'Body'). */
function essenceUsed(raw: unknown): string {
    let list: unknown = raw;
    if (typeof raw === 'string') { try { list = raw === '' ? [] : JSON.parse(raw); } catch { return ''; } }
    const values = Array.isArray(list) ? list : rec(list) ? Object.values(list as Rec) : [];
    for (const v of values) {
        const id = Array.isArray(v) ? v[1] : rec(v)?.['1'];
        let s: string;
        if (typeof id === 'string') s = id;
        else if (typeof id === 'number') s = String(id);
        else if (typeof id === 'boolean') s = id ? '1' : '';
        else continue;
        if (s.includes(' Essence')) return s.split(' ').find(Boolean) ?? '';
    }
    return '';
}

function isDamageOverTime(e: Env): boolean {
    return e.t1 === 'damage_over_time_roll' || e.t1 === e.R.rollTypes.damage_over_time_roll;
}

/** The live roll's effect tiers. */
function effectTiers(e: Env): AudioTiers {
    const { roll, ctx, R, t1, t2, rid, used, by, enc } = e;
    const tiers: AudioTiers = [];
    const want = (...keys: string[]): void => { const k = keys.filter(x => x !== ''); if (k.length) tiers.push(k); };
    const done = (...keys: string[]): AudioTiers => { want(...keys); want('die_roll'); return tiers; };
    const is = (...types: string[]): boolean => types.some(t => t !== '' && t === t1);
    const effectOnly = isCastEffectRow(roll);
    const own = (item: Rec | null): void => { for (const t of phaseTiers(ctx, item, effectOnly ? 'hit' : 'cast')) want(...t); };
    const RID = up(rid);
    const table = <T>(m: Record<string, T>, k: string): T | undefined => (has(m, k) ? m[k] : undefined);

    if (t1 === 'bouncing_spell_reused_attack' || t1 === 'arcane_arrow_reused_attack' || R.silent.includes(t1)) return tiers;
    if (is('raw', 'random_table', 'random_loot_table', 'random_encounter_table', 'random_name_table')) return done('die_roll');
    // This prerequisite is not a spell release, even when it succeeds or ties.
    // Its failure outcome arrives as a separate frame; never fall through to own(used).
    if (is(R.rollTypes.armor_bending_roll ?? '')) return done('die_roll');
    if (t1 === 'armor_bending_roll_failed' || t1 === 'spell_miscast') return [['spell/fizzle'], ['counterspell/fizzled']];

    if (isDamageOverTime(e)) {
        // The lingering status owns this impact; `used` can still name its applier.
        // Bare item pools in hit refs are cast takes, so keep only dedicated tick/impact refs.
        const status = ctx.def(RID) ?? used;
        const castPools = new Set([RID, up(itemField(used, 'item_id'))]);
        for (const tier of phaseTiers(ctx, status, 'hit')) want(...tier.filter(key => !castPools.has(key.toUpperCase())));
        return tiers;
    }
    if (is('damage', R.rollTypes.damage_roll ?? '', R.rollTypes.contesting_roll ?? '')) {
        if (!effectOnly) own(used);
        const attacker = up(itemField(by, 'item_id'));
        if (attacker && !effectOnly) want(`${attacker}/attack`);
        // The struck item: the one used when it carries a damage type, else roll_item_id (an inventory id → its template).
        const struck = layerOf(used, 'damage_type') ? used : (UUID.test(rid) ? (ctx.def(RID) ?? (() => { const t = inventoryTemplateId(ctx.inventory(RID)); return t ? ctx.def(t) : null; })()) : null);
        const struckId = up(itemField(struck, 'item_id'));
        for (const t of phaseTiers(ctx, struck, 'hit')) if (!(t.length === 1 && t[0] === struckId)) want(...t);
        return done();
    }
    if (t1 === 'weapon_attack_missed') {
        const whiff = phaseTiers(ctx, used, 'whiff');
        for (const t of whiff.length ? whiff : [['combat/miss_whiff']]) want(...t);
        return done();
    }
    if (t1 === 'spell_attack_missed') {
        const dt = layerOf(used, 'damage_type');
        const miss = dt ? Object.entries(ctx.taxonomy?.spell_miss ?? {}).find(([id]) => id.toUpperCase() === dt)?.[1] : undefined;
        if (miss) want(`miss/${miss}`);
        return done('combat/miss_whiff');
    }
    if (is('disarmed', 'immune_to_disarm')) {
        return done(`combat/${t1 === 'immune_to_disarm' ? 'armor_deflect' : 'weapon_clang'}`);
    }
    if (is(R.rollTypes.healing_roll ?? '')) { own(used); return done('spell/heal'); }
    // A regeneration tick: the source's own pool, then the approved Regenerating takes, then the generic heal.
    if (is(R.rollTypes.regeneration_roll ?? '')) { own(used); want('status/regenerating'); return done('spell/heal'); }
    if (is('encounter_loaded', 'party_pushed_to_encounter', 'targets_pushed_to_encounter', 'all_pushed_to_encounter')) return done('encounter_loaded#encounter_loaded');
    if (t1 === 'roll_initiative') return done('roll_initiative#roll_initiative');
    if (t1 === 'combat_ended') return done('end_combat#end_combat');
    if (is('skill', 'innate_skill', 'gathering_skill', 'knowledge_skill', 'crafting_skill', 'instrument_skill', 'weapon_skill', 'armor_skill',
        'defensive_skill', R.rollTypes.contesting_roll ?? '', R.rollTypes.skill_roll ?? '')) {
        const name = ({ Body: 'body', Mind: 'mind', Soul: 'soul' } as Record<string, string>)[essenceUsed(roll.rolls_full)];
        if (name) return done(`skill_roll#${name}`);
    }
    if (is('essence', 'defensive_roll')) {
        const name = table(R.essence, rid);
        if (name) return done(`skill_roll#${name}`);
    }
    if (is('self_status_effect_applied', 'status_effect_applied_to_target_after_cast', 'status_effect_applied')) {
        if (UUID.test(rid)) {
            want(`${RID}/applied`);
            const base = table(R.statusBase, RID);
            if (base) want(`${base}/applied`);
        }
        want('status_applied'); // …and on: the ONE OFFS / cast tail below still answer when no pool does
    }
    if (is('self_status_effect_deleted', 'self_status_effect_removed', 'status_effect_removed_to_target_after_cast',
        'status_effect_removed_after_contesting_roll_passed', 'status_effect_removed_from_target_after_cast', 'status_effect_removed')) {
        if (UUID.test(rid)) {
            want(`${RID}/removed`);
            const base = table(R.statusBase, RID);
            if (base) want(`${base}/removed`);
        }
        want('status_removed');
        return done(t1);
    }
    if (is('self_status_effect_applied', 'self_status_effect_deleted', 'status_effect_applied_to_target_after_cast', 'status_effect_removed_to_target_after_cast',
        'self_adjust_luck', 'ready', 'change_charges', 'adjust_charges', 'inventory_deleted')) return done(t1);
    if (t1 === 'creature_fled') return [['combat/flee']];
    // "Adjust Vitality" (resource_truth_commit_inc.php logs the signed delta as roll_result). It used to share
    // `spell/healing`, an EMPTY pool, so every adjust fell through to the die clatter — and most adjusts are harm
    // (20 of 23 on 2026-11-04), where a heal sound would be wrong anyway. Its own gain / loss pools.
    if (t1 === 'self_adjust_v') {
        const delta = int(roll.roll_result);
        return delta < 0 ? done('vital/health_loss') : delta > 0 ? done('vital/health_gain') : done();
    }
    const flat = table({ creature_resurrected: 'spell/revive', disengage: 'melee_attack/sword_on_sword_hit',
        creature_hidden: 'door_close', creature_revealed: 'spell/magic_blossom_short' } as Record<string, string>, t1);
    if (flat) return done(flat);
    if (is('creature_killed', 'creature_killed_forever')) {
        const creature = up(itemField(by, 'item_id'));
        if (creature) want(`${creature}/death`);
        const type = layerOf(by, 'creature_type');
        if (UUID.test(type)) want(`${type}/death`);
        return done('blood_spill');
    }
    if (is('move_inventory', 'equip_inventory', 'equip_both_hands', 'unequip_both_hands', 'swapped_hands', 'add_inventory')) return done('move_item/clothing');
    if (t1 === 'inventory_destroyed_rubble') {
        const inv = UUID.test(rid) ? ctx.inventory(RID) : null;
        if (inv) {
            let comp = layerOf(inv, 'item_composition');
            if (!UUID.test(comp)) { const t = inventoryTemplateId(inv); comp = t ? layerOf(ctx.def(t), 'item_composition') : ''; }
            if (UUID.test(comp) && !R.noRubble.includes(comp)) {
                const sub = table(R.composition, table(R.rubbleRemap, comp) ?? comp);
                if (sub) want(`rubble/${sub}`);
            }
        }
        return done();
    }
    if (t1 === 'end_turn') {
        if (t2 === '') return done('end_turn');
        if (t2 === 'other') return done('end_turn/other');
    }
    if (t1 === 'previous_turn') return done('turn/previous_turn');
    if (is('start_turn', 'start_legendary_turn')) return done('end_turn');
    if (is('gm_pushed_campaign_time', 'gm_pulled_campaign_time')) {
        const seconds = Math.abs(int(roll.roll_result));
        let tier = '1r';
        for (const [floor, name] of [[86400, '1d'], [28800, '8h'], [3600, '1h'], [1800, '30m'], [600, '10m'], [60, '1m']] as const) {
            if (seconds >= floor) { tier = name; break; }
        }
        return done(`time/${t1 === 'gm_pushed_campaign_time' ? 'push' : 'pull'}_${tier}`);
    }
    if (is('gm_changed_encounter_weather', 'gm_changed_encounter_light_type')) {
        const cat = t1 === 'gm_changed_encounter_weather' ? 'weather' : 'light';
        const vn = table(cat === 'weather' ? R.weather : R.light, RID);
        if (vn) want(`${cat}/${vn}`);
        return done(`${cat}/changed`);
    }
    if (is('the_sun_begins_to_rise', 'the_sun_has_risen', 'the_sun_begins_to_set', 'the_sun_has_set')) return done(`sun/${t1.slice('the_sun_'.length)}`);
    if (t1 === 'go_to_creature_turn') return done('turn/go_to_creature');
    const vote = table(R.partyVote, t1);
    if (vote !== undefined) { want(`party_vote/${vote}`); return tiers; } // a verdict is its pool or silence — never the die
    const flow = table(R.magicFlow, t1);
    if (flow) return done(`${flow[0]}/${flow[1]}`);
    if (is('updated_preferred_speed_type', 'speed_type_increased')) {
        const vn = table(R.speed, RID);
        if (vn) want(`speed/${vn}`);
        return done('speed/changed');
    }
    if (t1 === 'speed_type_moved') return footsteps(e, want, done);
    if (R.customAbility.includes(rid)) return done(`~/${rid}`);
    if (t1 === 'prepare_spell_list') return done('prepare_spell_list#prepare_spell_list');
    if (t1 === 'add_ap') return done('spell#Magic Game Abracadabra');
    if (t1 === 'add_reaction') return done('spell#Magic Game Abracadabra 2');
    if (is('10_second_warning', 'turn_timer_paused', 'turn_timer_resumed', 'turn_timer_reset', 'gm_added_turn_time')) return done('log#10 second warning');
    own(used); // other spell/ability outcomes: the item's own pool, from its cached refs
    const cue = table(R.eventCues, t1);
    if (cue) return done(`${cue[0]}/${cue[1]}`);
    return done();
}

/** speed_type_moved: footsteps by where the encounter is and how the mover moves. */
function footsteps(e: Env, want: (...k: string[]) => void, done: (...k: string[]) => AudioTiers): AudioTiers {
    const hidden = !!e.enc && Object.keys(e.enc).length > 0 && !!e.R.ids.hidden && statusIds(e.by).includes(e.R.ids.hidden);
    const tiers = footstepPoolKeys(e.ctx, e.t2, { hidden });
    for (const tier of tiers) want(...tier);
    return done();
}

/** Encounter surface rule. Without a gait, preserve the roll route's combat selector. */
export function footstepPoolKeys(env: Pick<RollSoundContext, 'encounter' | 'rules'>, speed: string,
    opts: { gait?: 'walk' | 'run' | 'sprint'; hidden?: boolean } = {}): AudioTiers {
    const R = env.rules, enc = env.encounter;
    const done = (...keys: string[]): AudioTiers => [keys];
    const sub = (s: string): AudioTiers => done(`${speed}/${s}`);
    const any = (subs: string[]): AudioTiers => done(...subs.map(s => `${speed}/${s}`));
    if (enc && Object.keys(enc).length > 0) {
        const hidden = opts.hidden === true;
        const inCombat = opts.gait ? opts.gait !== 'walk' : truthy(encLayer(enc, 'in_combat'));
        const inside = int(encLayer(enc, 'encounter_inside')) === 1 || int(encLayer(enc, 'location_inside')) === 1; // ft_encounter_is_inside_legacy
        const present = (k: string): boolean => encLayer(enc, k) !== null; // layer_has()
        const idIs = (k: string, ...names: string[]): boolean => { const v = up(encField(enc, k)); return v !== '' && names.some(n => (R.ids[n] ?? '') === v); };
        if (speed === 'speed_walking') {
            if (inside) {
                if (present('encounter_location_type')) {
                    if (idIs('encounter_location_type', 'cave', 'cave_system')) return sub(hidden ? 'walking_gravel_slow' : inCombat ? 'running_gravel' : 'walking_gravel');
                    if (idIs('encounter_location_type', 'dungeon')) return sub('walking_concrete');
                }
                if (present('encounter_environment')) {
                    if (idIs('encounter_environment', 'underground')) return sub(hidden ? 'walking_gravel_slow' : inCombat ? 'running_gravel' : 'walking_gravel');
                    if (idIs('encounter_environment', 'urban')) {
                        if (opts.gait && hidden) return sub('walking_gravel_slow');
                        return inCombat ? any(opts.gait ? ['running_hard_floor', 'running_wood'] : ['running_hard_floor', 'running_wood', 'walking_fast_wood']) : sub('walking_on_wood');
                    }
                }
            }
            if (truthy(encLayer(enc, 'encounter_outside'))) {
                if (present('encounter_weather') && idIs('encounter_weather', 'blizzard', 'snowing', 'snowing_and_raining')) {
                    return hidden ? sub('walking_carefully_on_ice') : inCombat ? sub('walking_snow_fast') : any(['walking_snow', 'walking_ice']);
                }
                if (present('encounter_environment')) {
                    if (idIs('encounter_environment', 'forest') && hidden) return sub('walking_leaves_sneak');
                    if (idIs('encounter_environment', 'swamp')) return sub(hidden ? 'walking_swamp_half_immersed' : 'walking_swamp');
                    if (idIs('encounter_environment', 'underwater')) return sub('walking_shallow_water');
                    if (idIs('encounter_environment', 'urban')) return sub(hidden ? 'walking_gravel_slow' : inCombat ? 'running_gravel' : 'walking_gravel');
                }
            }
            return sub(hidden ? 'walking_gravel_slow' : inCombat ? 'running_gravel' : 'walking_gravel');
        }
        if (speed === 'speed_climbing') return sub(inside ? 'metal' : 'wood');
    }
    return done(speed);
}

// ── voice (get_caster_voice_audio_info / get_target_voice_audio_info / ft_impact_voice_line) ───────────────
function voiceSlug(R: RollSoundRules, arr: unknown): string {
    const vid = voiceIdOf(arr);
    if (!vid) return '';
    if (R.ids.silenced && statusIds(arr).includes(R.ids.silenced)) return '';
    return R.voices[vid] ?? '';
}
function voiceTake(slug: string, suffixes: string[]): AudioTiers {
    const tiers: AudioTiers = [];
    for (const lane of ['voice/', 'voice~/']) for (const s of suffixes) tiers.push([`${lane}${slug}__${s}`]);
    return tiers;
}
const slugify = (s: string): string => s.replace(/[A-Z]/g, c => c.toLowerCase()).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const htmlSpecialChars = (s: string): string => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#039;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The kept NATURAL face of a row's main d20, or null. Twin of ft_roll_natural_d20_face(): the main die's
 * label starts with `d20`, a discarded ADV/DIS die is `kept: false`, and a FORCED face (a roll-at-max
 * status) is skipped — that was not luck, so it earns no bark.
 */
export function naturalD20Face(rollsFull: unknown): number | null {
    let tuples: unknown = rollsFull;
    if (typeof rollsFull === 'string') { try { tuples = JSON.parse(rollsFull); } catch { return null; } }
    if (!Array.isArray(tuples)) return null;
    for (const tuple of tuples) {
        if (!Array.isArray(tuple) || tuple.length < 2 || typeof tuple[1] !== 'string') continue;
        const label = tuple[1].toLowerCase();
        if (!label.startsWith('d20') || label.includes('forced')) continue;
        const meta = rec(tuple[2]);
        if (meta && meta.kept === false) continue;
        // Only a whole number is a face ("20junk", null, 20.5, true are not) — an invalid tuple is skipped,
        // never cast, so a later valid die still counts. Identical rule in ft_roll_natural_d20_face().
        const raw: unknown = tuple[0];
        if (typeof raw === 'number' && Number.isInteger(raw)) return raw;
        if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number(raw);
    }
    return null;
}

/** PHP is_numeric() for a flat scalar (an int, a float, or a numeric string with optional surrounding space). */
function isNumeric(v: unknown): boolean {
    if (typeof v === 'number') return Number.isFinite(v);
    return typeof v === 'string' && /^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(v);
}

const ATTACK_ROLL_TYPES = ['weapon_melee_attack_roll', 'weapon_ranged_attack_roll', 'spell_melee_attack_roll', 'spell_ranged_attack_roll'];

/**
 * One creature SPOTTING another: `target_creature_revealed` with two readable, DIFFERENT actors (speaking in chat logs
 * the speaker on both sides). Twin of ft_voice_row_is_spot_reveal().
 */
function isSpotReveal(e: Env): boolean {
    if (e.t1 !== 'target_creature_revealed') return false;
    const b = actorId(e.by), t = actorId(e.to);
    return b !== '' && t !== '' && b !== t;
}

/**
 * The reaction bark a row earns for its ACTOR (owner 2026-09-16), or ''. Twin of ft_voice_reaction_suffix():
 * a natural 20 / 1 on a PLAYER CHARACTER's attack (attacks only, PCs only), or a skill tier genuinely gained.
 * `can_level_up` is not here — its actor is the GM, so that line rides the target slot (impactLine).
 */
function reactionSuffix(e: Env): string {
    const { R, roll, t1, by } = e;
    if (!t1) return '';
    // A STEALTH SPOT: the spotter calls it — anyone with a voice (owner 2026-09-16). Before the PC gate on purpose.
    if (isSpotReveal(e)) return 'emote_reveal';
    // PCs only, for every other reaction (owner 2026-09-16).
    if (!isPlayerCharacter(by)) return '';
    if (t1 === R.rollTypes.contesting_roll && hostilePair(by, e.to)
        && isNumeric(roll.roll_result) && isNumeric(roll.roll_dc) && Number(roll.roll_result) > Number(roll.roll_dc)
        && fnv1a32(`${text(roll.roll_id)}|resisted`) % 100 < (Number(R.impact.resisted) || 0)) return 'emote_resisted';
    if (t1 === 'training_skill_tier_up' || t1 === 'ascension_skill_tier_up') {
        // roll_dc = old level, roll_result = new level: both present, and the level must strictly RISE.
        if (!isNumeric(roll.roll_dc) || !isNumeric(roll.roll_result)) return '';
        return Math.trunc(Number(roll.roll_result)) > Math.trunc(Number(roll.roll_dc)) ? 'emote_levelup' : '';
    }
    const isAttack = !!e.attack || ATTACK_ROLL_TYPES.some(vn => { const id = R.rollTypes[vn]; return !!id && id === t1; });
    if (!isAttack) return '';
    const face = e.attack ? e.attack.natural : naturalD20Face(roll.rolls_full);
    return face === 20 ? 'emote_crit' : face === 1 ? 'emote_fumble' : '';
}

/**
 * Which reaction-bark lines a frame earns: `caster` for the actor (crit / fumble / tier-up), `target` for the
 * level-up spoken by the PC on roll_to. The parity test runs this against ft_voice_reaction_suffix() and
 * ft_impact_voice_line() on the same frames, so the two sides cannot drift.
 */
export function rollReactionLines(roll: Rec, rules: RollSoundRules, by: Rec | null, to: Rec | null): { caster: string; target: string } {
    const e: Env = { roll, ctx: { rules } as RollSoundContext, R: rules, t1: text(roll.roll_type_1), t2: text(roll.roll_type_2),
        rid: '', used: null, noUsed: true, by, to, enc: null };
    // The REAL resolvers — a re-implementation here would let the test pass while production drifted.
    return { caster: reactionSuffix(e), target: impactLine(e) };
}

/** The caster route's full tier order for a frame — the parity test compares it with ft_caster_voice_pools(). */
export function rollCasterVoiceTiers(roll: Rec, rules: RollSoundRules, by: Rec | null, to: Rec | null, used: Rec | null): AudioTiers {
    const e: Env = { roll, ctx: { rules } as RollSoundContext, R: rules, t1: text(roll.roll_type_1), t2: text(roll.roll_type_2),
        rid: '', used, noUsed: !used, by, to, enc: null };
    return casterVoice(e);
}

const CONCENTRATION_EXPIRY_TYPES = ['status_effect_expired', 'effect_ends_on_target_end_of_turn', 'effect_ends_on_target_start_of_turn'];
/**
 * The caster's CONCENTRATION-ENDS bark (director 2026-09-22), or '': `emote_concentration_broken` when it was forced,
 * `emote_concentration_released` when it was chosen or ran its course. Twin of ft_voice_concentration_end_suffix().
 */
export function concentrationEndSuffix(roll: Rec, R: RollSoundRules): string {
    const t1 = text(roll.roll_type_1);
    if (t1 === 'concentration_broken') return 'emote_concentration_broken';
    if (t1 === 'end_concentration' || t1 === 'no_charges_to_maintain') return 'emote_concentration_released';
    if (!CONCENTRATION_EXPIRY_TYPES.includes(t1)) return '';
    const concentrating = (R.ids.concentrating ?? '').toUpperCase();
    return concentrating !== '' && text(roll.roll_item_id).toUpperCase() === concentrating ? 'emote_concentration_released' : '';
}

/**
 * The caster's ARMOR-BENDING-FAILED bark (director 2026-10-09), or '': `emote_armor_bending_failed` on the
 * `armor_bending_roll_failed` row (caster → caster, the fizzled spell's id). Twin of ft_voice_armor_bending_failed_suffix().
 */
export function armorBendingFailedSuffix(roll: Rec): string {
    return text(roll.roll_type_1) === 'armor_bending_roll_failed' ? 'emote_armor_bending_failed' : '';
}

/** The caster EVENT line a row speaks INSTEAD of its item's incantation, or ''. Twin of ft_voice_caster_event_suffix(). */
export function casterEventSuffix(roll: Rec, R: RollSoundRules): string {
    return concentrationEndSuffix(roll, R) || armorBendingFailedSuffix(roll)
        || (text(roll.roll_type_1) === 'is_conscious' ? 'emote_revived' : '');
}

/**
 * CHARGE BOOKKEEPING rows name the item whose count moved — a sheet edit, a ± click, a spend or grant inside another
 * action — but none of them is a USE of it, so the holder never speaks its line (Geno 2026-10-20: editing Short Rest's
 * charges said the Short Rest bark). The use's own row speaks; the rows keep their SFX cue (effectTiers). The PHP
 * twin ft_caster_voice_pools() is a test oracle and does not carry this.
 */
const CHARGE_ROW_TYPES: ReadonlySet<string> = new Set(['change_charges', 'adjust_charges']);

function casterVoice(e: Env): AudioTiers {
    const { R, roll, used, noUsed, by, to } = e;
    if (!by || isDamageOverTime(e) || CHARGE_ROW_TYPES.has(e.t1)) return [];
    // A caster EVENT line speaks ITS line and nothing else — the row's item is the attacker's spell on a concentration
    // damage break, or the spell that just fizzled on a failed armor bending roll.
    const event = casterEventSuffix(roll, R);
    if (event) {
        if (event === 'emote_revived' && !isPlayerCharacter(by)) return [];
        const s = voiceSlug(R, by);
        return s ? voiceTake(s, [event]) : [];
    }
    // The frame's item name stands in only when the row DID use an item this page holds no definition for.
    const fallbackName = noUsed ? '' : text(roll.roll_item_id_item_name);
    const name = rec(roll.roll_sound)?.incantation === 0 || isCastEffectRow(roll) ? '' : ((used ? itemField(used, 'item_name') : '') || fallbackName);
    // A reaction needs no item: an unarmed or item-less attack row can still crow on a natural 20.
    const reaction = reactionSuffix(e);
    if (!name && !reaction) return [];
    const slug = voiceSlug(R, by);
    if (!slug) return [];
    // REACTION BARKS are their own LAST group, not an extra candidate: voiceTake() walks every approved
    // candidate before any unreviewed one, so appending would let an APPROVED crit bark beat an UNREVIEWED
    // incantation. Twin: ft_caster_voice_pools().
    const tail = reaction ? voiceTake(slug, [reaction]) : [];
    if (!name) return tail;
    const spell = slugify(name);
    const legacy = slugify(htmlSpecialChars(name));
    const candidates = [spell];
    if (legacy !== spell) candidates.push(legacy);
    if (has(R.voiceAliases, spell)) candidates.push(R.voiceAliases[spell]!);
    // ACTION → EMOTE only for something the actor did to ITSELF; an unreadable pair is silence.
    const selfDirected = !to ? !text(roll.roll_to) : (() => { const b = actorId(by), t = actorId(to); return b !== '' && t !== '' && b === t; })();
    if (has(R.actionEmotes, spell) && selfDirected) candidates.push(R.actionEmotes[spell]!);
    return [...voiceTake(slug, candidates), ...tail];
}

/** Readable creature actors on known, different teams. Items and inventories cannot earn hostile barks. */
function hostilePair(by: Rec | null, to: Rec | null): boolean {
    return !!actorId(by) && !!actorId(to) && !!teamOf(by) && !!teamOf(to) && teamOf(by) !== teamOf(to);
}

/** Extra caster line: keep the incantation/reaction and its once-slot intact. Twin: ft_voice_kill_suffix(). */
function killSuffix(e: Env): string {
    const { R, roll, by, to, t1 } = e;
    if (!t1 || t1 !== R.rollTypes.damage_roll || !isPlayerCharacter(by) || isPlayerCharacter(to) || !hostilePair(by, to)) return '';
    const cur = vitality(to, 'v_current'), temp = vitality(to, 'v_temp'), dmg = int(roll.roll_result);
    if (cur <= 0 || dmg <= 0 || Math.max(0, cur - Math.max(0, dmg - temp)) > 0) return '';
    return fnv1a32(`${text(roll.roll_id)}|kill`) % 100 < (Number(R.impact.kill) || 0) ? 'emote_kill' : '';
}

/** The browser arrival snapshot is PRE-heal; PHP's helper subtracts applied from its POST-heal row. */
function healTargetSuffix(e: Env): string {
    const { R, roll, by, to, t1 } = e;
    if (!t1 || t1 !== R.rollTypes.healing_roll || !isPlayerCharacter(to) || !actorId(to) || !actorId(by)
        || actorId(by) === actorId(to)) return '';
    const feedback = rec(roll.roll_feedback);
    const applied = int(feedback && has(feedback, 'applied') ? feedback.applied : roll.roll_result);
    if (applied <= 0) return '';
    const a = rec(to), computed = rec(a?.computed);
    const vit = rec(computed?.vitality) ?? rec(computed?.v_arr) ?? rec(a?.vitality);
    // Missing vitality is unknown, never proof of a revival. Thanks still works in that case.
    if (isNumeric(unwrap(vit?.v_current)) && vitality(to, 'v_current') === 0) return 'emote_revived';
    return fnv1a32(`${text(roll.roll_id)}|thanks`) % 100 < (Number(R.impact.thanks) || 0) ? 'emote_thanks_heal' : '';
}

/** Facts needed for both low-health and ally-down routing; no holder branches survive in these records. */
function allyFacts(row: Rec, me: string, team: string): AllyFacts | null {
    const id = actorId(row);
    if (!id || id === me || teamOf(row) !== team || isDead(row) || vitality(row, 'v_current') <= 0) return null;
    const pc = isPlayerCharacter(row);
    return { id, dead: int(row.encounter_creature_dead) === 1, pc, voiceId: pc ? voiceIdOf(row) : '',
        // The silenced id comes from the not-yet-loaded catalog. Retain membership as a scalar, not status rows.
        statuses: pc ? statusIds(row).join('\x1f') : '' };
}
function allyVoice(R: RollSoundRules, ally: AllyFacts): string {
    return R.ids.silenced && ally.statuses.split('\x1f').includes(R.ids.silenced) ? '' : R.voices[ally.voiceId] ?? '';
}
function alliesOf(e: Env): readonly AllyFacts[] {
    if (e.ctx.allies) return e.ctx.allies;
    const me = actorId(e.to), team = teamOf(e.to), allies: AllyFacts[] = [];
    if (me && team) for (const row of e.ctx.creatures ?? rosterOf(e.enc)) {
        if (!rec(row)) continue;
        const facts = allyFacts(row, me, team);
        if (facts) allies.push(facts);
    }
    return allies;
}

/** One voiced, conscious PC ally, selected independently of roster order. Twin: ft_impact_voice_pick_ally(). */
function downAlly(e: Env): AllyFacts | null {
    if (impactLine(e) !== 'death_cry' || vitality(e.to, 'v_current') <= 0) return null;
    let chosen: AllyFacts | null = null, best = Infinity;
    for (const ally of alliesOf(e)) {
        if (ally.dead || !ally.pc || !allyVoice(e.R, ally)) continue;
        const score = fnv1a32(`${text(e.roll.roll_id)}|ally|${ally.id}`);
        if (score < best || (score === best && ally.id < (chosen?.id ?? ''))) { chosen = ally; best = score; }
    }
    return chosen;
}

const isLow = (cur: number, max: number): boolean => !(max > 0) || cur / max <= 0.10; // calc_v_status → on_deaths_door | dying

function impactLine(e: Env): string {
    const { R, roll, to, t1 } = e;
    if (!to || !t1) return '';
    // A LEVEL-UP is spoken by the character who levelled: the row's actor is the GM, the PC is roll_to.
    if (t1 === 'can_level_up') return isPlayerCharacter(to) ? 'emote_levelup' : '';
    // A PARTY LONG REST: the one voiced rester the server put on roll_to says the rest line. Twin: ft_impact_voice_line().
    if (t1 === 'party_long_rested') return 'emote_rest';
    // A STEALTH SPOT: the creature caught answers, whoever it is; rollSoundRoute stacks it after the spotter's line.
    if (isSpotReveal(e)) return 'emote_spotted';
    if (t1 === R.rollTypes.healing_roll) return healTargetSuffix(e);
    const damageRolls = [R.rollTypes.damage_roll ?? '', R.rollTypes.damage_over_time_roll ?? ''].filter(Boolean);
    if (!damageRolls.includes(t1)) return '';
    const dmg = int(roll.roll_result);
    if (dmg <= 0) return '';
    const cur = vitality(to, 'v_current'), max = vitality(to, 'effective_v_max'), temp = vitality(to, 'v_temp');
    if (max <= 0) return '';
    // The damage row arrives BEFORE the damage lands on the row, so `to` still holds the pre-hit vitality.
    const tempAfter = Math.max(0, temp - dmg);
    const curAfter = Math.max(0, cur - Math.max(0, dmg - temp));
    void tempAfter;
    if (curAfter <= 0) return isPlayerCharacter(to) ? 'death_cry' : '';
    if (!isLow(cur, max) && isLow(curAfter, max)) return alliesOf(e).length ? 'emote_need_healing' : 'emote_low_health';
    if (fnv1a32(`${text(roll.roll_id)}|impact`) % 100 >= (Number(R.impact.chance) || 0)) return '';
    return dmg >= (Number(R.impact.heavy) || 0) * max ? 'impact_heavy' : 'impact_light';
}

/**
 * The route for one roll frame, or null when it makes no sound: `sfx` tiers, plus the voice tiers the browser applies
 * its precedence to (caster first, once per cast; the target only when the caster has no take).
 */
export function rollSoundRoute(roll: Rec, ctx: RollSoundContext): RollRoute | null {
    const e = gather(roll, ctx);
    const out: RollRoute = { sfx: rec(roll.roll_sound)?.sfx === 0 ? [] : dedupe(effectTiers(e)) };
    if (rec(roll.roll_sound)?.voice !== 0) {
        const voice: NonNullable<RollRoute['voice']> = {};
        const caster = dedupe(casterVoice(e));
        // A concentration end has its OWN once-slot: its row shares roll_id AND roll_by with the same creature's earlier
        // rows of the event (its concentration save, an Evade, a damage soak), so under the plain key whichever of those
        // spoke first swallowed the break line — and the break would in turn silence the creature's next line.
        // A failed armor bending roll likewise shares roll_id + roll_by with the cast's armor_bending_roll row, which
        // already spoke the spell's incantation — so the frustration line needs its own slot too.
        const concentrationEnd = concentrationEndSuffix(roll, ctx.rules);
        const armorBendingFailed = armorBendingFailedSuffix(roll);
        const slot = concentrationEnd ? '|concentration' : armorBendingFailed ? '|armor_bending_failed'
            : text(roll.roll_type_1) === 'is_conscious' ? '|revived' : reactionSuffix(e) === 'emote_resisted' ? '|resisted' : '';
        if (caster.length) { voice.caster = caster; voice.once = `${text(roll.roll_id)}|${text(roll.roll_by)}${slot}`; }
        const line = impactLine(e);
        const slug = line ? voiceSlug(ctx.rules, e.to) : '';
        if (slug) voice.target = voiceTake(slug, [line]);
        if (voice.target && ((voice.caster && isSpotReveal(e)) || e.t1 === ctx.rules.rollTypes.healing_roll)) voice.stack = true;
        const kill = killSuffix(e), killer = kill ? voiceSlug(ctx.rules, e.by) : '';
        if (killer) {
            voice.casterAfter = { tiers: voiceTake(killer, [kill]), once: `${text(roll.roll_id)}|${text(roll.roll_by)}|kill` };
            voice.queue = true;
        }
        const ally = downAlly(e);
        if (ally) voice.ally = { tiers: voiceTake(allyVoice(ctx.rules, ally), ['emote_ally_down']),
            once: `${text(roll.roll_id)}|${ally.id}|ally_down` };
        // A concentration end WAITS for the line already speaking (director: the hit's grunt, then the line) — its
        // row lands right behind the damage row, and starting at once would cut the grunt off. A failed armor bending
        // roll waits the same way for the incantation its armor_bending_roll row is still speaking.
        if (voice.caster && slot) voice.queue = true;
        if (voice.caster || voice.target || voice.casterAfter || voice.ally) out.voice = voice;
    }
    return out.sfx.length || out.voice ? out : null;
}

/**
 * The voice cues of an action the ACTOR resolved itself (the client-truth lanes), by the same rules a roll's route
 * uses: the caster's line for the item it used, else — on a hit that deals `damage` — the target's body line from its
 * PRE-hit row (call before the result applies). `seed` stands in for roll_id in the impact coin.
 * Client-truth picks/relays carry exactly ONE voice (pickActionVoice / actionSoundTakes), so the KILL line cannot follow
 * the attacker's line here as it does on a roll (casterAfter). It is the attacker's LAST caster group instead: a crit or
 * incantation still wins, and the dying enemy has no line of its own (death_cry is PC-only, and a PC target earns no kill).
 * The ally-down shout needs a third speaker, so it stays roll-route only.
 */
export function actionVoiceTiers(ctx: RollSoundContext, input: { by: Rec | null; used: Rec | null; to?: Rec | null; damage?: number; seed: string; naturalD20?: number }): { caster?: AudioTiers; target?: AudioTiers } {
    const to = input.to ?? null;
    const damage = Math.max(0, Math.trunc(Number(input.damage) || 0));
    const roll: Rec = { roll_type_1: damage > 0 ? (ctx.rules.rollTypes.damage_roll ?? '') : 'no_roll', roll_id: input.seed,
        roll_result: damage, roll_by: actorId(input.by), roll_to: to ? actorId(to) : '' };
    const e: Env = { roll, ctx, R: ctx.rules, t1: text(roll.roll_type_1), t2: '', rid: '', used: input.used, noUsed: !input.used, by: input.by, to, enc: ctx.encounter,
        // The lane resolved an ATTACK; its synthetic roll has no rolls_full, so the face comes in directly.
        ...(Number.isFinite(input.naturalD20) ? { attack: { natural: Math.trunc(Number(input.naturalD20)) } } : {}) };
    const kill = killSuffix(e), killer = kill ? voiceSlug(ctx.rules, input.by) : '';
    const caster = dedupe([...casterVoice(e), ...(killer ? voiceTake(killer, [kill]) : [])]);
    const line = impactLine(e);
    const slug = line ? voiceSlug(ctx.rules, to) : '';
    return { ...(caster.length ? { caster } : {}), ...(slug ? { target: voiceTake(slug, [line]) } : {}) };
}

const WARM_VOICE_CAP = 60;
/**
 * Every voice the page's encounters' creatures speak in (silenced ones excluded) — what to warm for instant action voices.
 * The active encounter first, then every other encounter the page holds: a GM acts in any encounter it has open, and a
 * cell-pinned page's active encounter may not be the one it plays in.
 */
export function encounterVoiceSlugs(catalog: AudioCatalog): string[] {
    const rules = rollSoundRules(catalog.rules);
    const ft = rec((globalThis as unknown as Rec).FateTide);
    if (!rules || !ft) return [];
    const blobs: Rec[] = [];
    const active = rec(ft.encounterArr);
    if (active) blobs.push(active);
    const cache = ft.encounterCache as Store | undefined;
    if (typeof cache?.values === 'function') for (const blob of cache.values()) { const b = rec(blob); if (b && b !== active) blobs.push(b); }
    const slugs = new Set<string>();
    for (const blob of blobs) {
        for (const row of rosterOf(blob)) {
            const slug = voiceSlug(rules, row);
            if (slug) slugs.add(slug);
            if (slugs.size >= WARM_VOICE_CAP) return [...slugs];
        }
    }
    return [...slugs];
}

/**
 * The voice ONE creature speaks in, from the page's own encounter blobs, or '' (voiceless, silenced, or not on this
 * page). The same rule encounterVoiceSlugs() walks — what lets a chat emote speak the instant the sender acts, before
 * the server has said a word about who that is. Server twin: ft_voice_slug_of().
 */
export function entityVoiceSlug(catalog: AudioCatalog, entityId: string): string {
    const rules = rollSoundRules(catalog.rules);
    const ft = rec((globalThis as unknown as Rec).FateTide);
    const want = entityId.trim().toUpperCase();
    if (!rules || !ft || !want) return '';
    const blobs: Rec[] = [];
    const active = rec(ft.encounterArr);
    if (active) blobs.push(active);
    const cache = ft.encounterCache as Store | undefined;
    if (typeof cache?.values === 'function') for (const blob of cache.values()) { const b = rec(blob); if (b && b !== active) blobs.push(b); }
    for (const blob of blobs) {
        for (const row of rosterOf(blob)) {
            if (actorId(row).toUpperCase() === want) return voiceSlug(rules, row);
        }
    }
    return '';
}

// ── the page's context ───────────────────────────────────────────────────────────────────────────────────────
type Store = { get?: (k: string) => unknown; values?: () => IterableIterator<unknown> };
const INVENTORY_WALK_LIMIT = 20000;
const INVENTORY_KEYS = ['encounter_inventories_arr', 'inventory', 'inventory_arr', 'contents', 'inventories'];

/**
 * Held roster/catalog references, named participant snapshots and minimal ally arrival facts. A cold catalog still
 * sees the pre-hit target and the inventory a destruction removed without copying unrelated holders.
 */
export interface RollSoundCapture { enc: Rec | null; rows: readonly Rec[]; pinned: Map<string, Rec | null>; allies: AllyFacts[] }

/** The roll's own encounter: the page's cached blob for its encounter_id, else the active one when the ids agree. */
export function pageEncounter(roll: Rec): Rec | null {
    const ft = rec((globalThis as unknown as Rec).FateTide);
    const encId = up(roll.encounter_id);
    const cache = ft?.encounterCache as Store | undefined;
    const active = rec(ft?.encounterArr);
    let enc: Rec | null = null;
    if (encId && typeof cache?.get === 'function') enc = rec(cache.get(encId)) ?? rec(cache.get(encId.toLowerCase())) ?? rec(cache.get(text(roll.encounter_id)));
    if (!enc && active && (!encId || up(encField(active, 'encounter_id')) === encId)) enc = active;
    return enc;
}
const rosterOf = (enc: Rec | null): readonly Rec[] =>
    enc && Array.isArray(enc.encounter_creatures_arr) ? enc.encounter_creatures_arr as Rec[] : [];
const isCreatureRow = (r: Rec | null): boolean => !!r && text(rec(r.encounter_creatures)?.encounter_creature_id) !== '';

/** One bounded inventory walk per arrival capture. Count-only caches miss same-size holder replacements. */
function inventoryIndex(enc: Rec, rows: readonly Rec[]): Map<string, Rec> {
    const loose = Array.isArray(enc.encounter_inventories_arr) ? enc.encounter_inventories_arr as unknown[] : [];
    const index = new Map<string, Rec>();
    let budget = INVENTORY_WALK_LIMIT;
    const walk = (node: unknown, depth: number): void => {
        if (--budget < 0 || depth > 6 || !node || typeof node !== 'object') return;
        if (Array.isArray(node)) { for (const n of node) walk(n, depth + 1); return; }
        const r = node as Rec;
        const id = up(rec(r.inventory)?.inventory_id) || up(r.inventory_id);
        if (id && !index.has(id)) index.set(id, r);
        for (const key of INVENTORY_KEYS) {
            const child = r[key];
            // `inventory` is a row's own bucket when it is an object, a creature's carried list when it is an array.
            if (child && typeof child === 'object' && (key !== 'inventory' || Array.isArray(child))) walk(child, depth + 1);
        }
    };
    walk(loose, 0);
    walk(rows, 0);
    return index;
}

/** A row with its vitality copied out, so a later in-place update cannot rewrite what this roll saw. */
function frozenRow(row: Rec): Rec {
    const computed = rec(row.computed);
    const vit = rec(computed?.vitality);
    const vArr = rec(computed?.v_arr), legacy = rec(row.vitality);
    return { ...row, ...(rec(row.encounter_creatures) ? { encounter_creatures: { ...rec(row.encounter_creatures) } } : {}),
        ...(computed ? { computed: { ...computed, ...(vit ? { vitality: { ...vit } } : {}), ...(vArr ? { v_arr: { ...vArr } } : {}) } } : {}),
        ...(legacy ? { vitality: { ...legacy } } : {}) };
}

/** Take the arrival snapshot. Synchronous and cheap: call it the moment the frame lands, before any await. */
export function captureRollSoundContext(roll: Rec): RollSoundCapture {
    const enc = pageEncounter(roll);
    const rows = rosterOf(enc);
    const pinned = new Map<string, Rec | null>();
    const sound = rec(roll.roll_sound);
    const named = new Set([roll.roll_by, roll.roll_to, roll.roll_item_id, sound?.used === 0 ? '' : sound?.used].map(up).filter(Boolean));
    if (!named.size) return { enc, rows, pinned, allies: [] };
    let target: Rec | null = null;
    for (const row of rows) {
        if (!rec(row)) continue;
        const id = up(actorId(row));
        if (named.has(id) && !pinned.has(id)) pinned.set(id, frozenRow(row));
        if (id === up(roll.roll_to)) target = row;
    }
    const allies: AllyFacts[] = [], me = actorId(target), team = teamOf(target);
    if (sound?.voice !== 0 && me && team) for (const row of rows) {
        if (!rec(row)) continue;
        const facts = allyFacts(row, me, team);
        if (facts) allies.push(facts);
    }
    let index: Map<string, Rec> | null = null;
    for (const k of named) {
        if (pinned.has(k)) continue;
        index ??= enc ? inventoryIndex(enc, rows) : null;
        const row = index?.get(k);
        // Only a target inventory is read for vitality; item/used lookups need its held template reference.
        pinned.set(k, row && k === up(roll.roll_to) ? frozenRow(row) : row ?? null);
    }
    return { enc, rows, pinned, allies };
}

/** Build the context from what this page holds: the arrival snapshot, the roll's encounter blob, the definition caches. */
export function pageRollSoundContext(roll: Rec, catalog: AudioCatalog, capture: RollSoundCapture = captureRollSoundContext(roll)): RollSoundContext | null {
    const rules = rollSoundRules(catalog.rules);
    if (!rules) return null;
    const ft = rec((globalThis as unknown as Rec).FateTide);
    const { enc, rows, pinned, allies } = capture;
    const isDef = (v: unknown): Rec | null => { const r = rec(v); return r && rec(r.items) ? r : null; };
    const entryOf = (root: unknown, k: string): unknown => { const c = rec(rec(root)?.catalog); return c ? c[k] ?? c[k.toLowerCase()] : undefined; };
    const fromCatalog = (root: unknown, k: string): Rec | null => isDef(entryOf(root, k));
    const defs = new Map<string, Rec | null>();
    const findDef = (k: string): Rec | null => {
        const hit = fromCatalog(enc, k);
        if (hit) return hit;
        for (const row of rows) { const d = fromCatalog(row, k); if (d) return d; }
        for (const name of ['objectCache', 'casterCache']) {
            const store = ft?.[name] as Store | undefined;
            if (!store) continue;
            const direct = typeof store.get === 'function' ? (isDef(store.get(k)) ?? isDef(store.get(k.toLowerCase()))) : null;
            if (direct && up(itemField(direct, 'item_id')) === k) return direct;
            if (typeof store.values === 'function') for (const root of store.values()) { const d = fromCatalog(root, k); if (d) return d; }
        }
        // An identity-only catalog entry ({item_name, item_type}) still names the item's kind: enough for its own
        // pool (refs `cast` = the item id) and its voice line, though not for layer-derived tiers.
        const identity = rec(entryOf(enc, k));
        if (identity && typeof identity.item_type === 'string') return { items: { item_id: k, item_type: identity.item_type, item_name: text(identity.item_name) } };
        return null;
    };
    return {
        rules,
        taxonomy: catalog.taxonomy ?? null,
        encounter: enc,
        // Action voices reuse this context with their own target; only a roll names an arrival target here.
        allies: text(roll.roll_to) ? allies : undefined,
        def: id => { const k = up(id); if (!k) return null; if (!defs.has(k)) defs.set(k, findDef(k)); return defs.get(k) ?? null; },
        row: id => {
            const k = up(id);
            if (!k) return null;
            if (pinned.has(k)) { const v = pinned.get(k) ?? null; return isCreatureRow(v) ? v : null; }
            return rows.find(r => rec(r) && up(actorId(r)) === k) ?? null;
        },
        inventory: id => {
            const k = up(id);
            if (!k) return null;
            if (pinned.has(k)) { const v = pinned.get(k) ?? null; return v && !isCreatureRow(v) ? v : null; }
            return enc ? inventoryIndex(enc, rows).get(k) ?? null : null;
        },
    };
}
